import {DomainBulkExtensionQueryQuery} from "./bulkextensionqueries.generated";
import {LocationsQuery} from "@/queries.generated";
import {saveAs} from "file-saver";
import {defineMessage, IntlShape, MessageDescriptor} from "react-intl";
import {BulkUserModificationData, LocationData} from "@/api";
import {EXCEL_MAX_ROWS, TextFormat, removeExcelEmailLink} from "@/utils/excel";
import {type Workbook} from "exceljs";
import {DateTime} from "luxon";
import {labelForUserPackage} from "@/utils/translations";
import {getRandomString} from "@/tools";

const EXCEL_CELL_DATETIME_FORMAT = "YYYY.MM.DD. HH:MM:SS" as const;
const EXCEL_FILE_DATETIME_FORMAT = "yyyy-MM-dd-HH-mm-ss" as const;

type User = DomainBulkExtensionQueryQuery["usersBulk"][number];
type UserModification = BulkUserModificationData;
type LocationModification = LocationData;

// some types to help with the columns definition and consistency
// it is enough for these 2, as they are the only ones being modified by the user
// therefore being sensitive
type UsersSheetColumnKey =
    | "short_number"
    | "display_name"
    | "e164"
    | "email"
    | "home_humber"
    | "mobile_humber"
    | "provisioned_mac_address"
    | "provisioned_vendor"
    | "language"
    | "location"
    | "user_package";

type LocationsSheetColumnKey =
    | "location_name"
    | "description"
    | "pilot_number"
    | "parallel_call_limit"
    | "bandwidth_limit"
    | "ip_address";

export type ExcelPermissions = {
    canModifyUsers: boolean;
    canManageProvisionedDevices: boolean;
    canAddUsers: boolean;
    canAddLocations: boolean;
    canAddUserLogins: boolean;
    canMoveUsers: boolean;
};

// 'style' is for making them text instead of numbers
// 'width' is not necessary here, but it is for the sake of consistency
const COLUMN_FORMAT = {
    width: 30,
    style: TextFormat,
};

const getProvisionedColumnTitle = (intl: IntlShape, suffixDesc: MessageDescriptor, lowercase?: boolean): string => {
    const provisionedDevice = intl.formatMessage({id: "provisioned_device", defaultMessage: "Provisioned device"});
    let suffix = intl.formatMessage(suffixDesc);
    if (lowercase) {
        suffix = suffix.toLowerCase();
    }

    return `${provisionedDevice} ${suffix}`;
};

const formatMacAddress = (raw: string): string => raw?.match(/.{1,2}/g)?.join(":") ?? raw;

// This mapping only used to determine whether to lock or unlock a cell for a domain admin
// in the users sheet
const USERS_CELL_UNLOCK_PERMISSION_MAP: Record<UsersSheetColumnKey, keyof ExcelPermissions> = {
    short_number: "canAddUsers",
    display_name: "canModifyUsers",
    e164: "canModifyUsers",
    email: "canAddUserLogins",
    home_humber: "canModifyUsers",
    mobile_humber: "canModifyUsers",
    provisioned_mac_address: "canManageProvisionedDevices",
    provisioned_vendor: "canManageProvisionedDevices",
    language: "canAddUserLogins",
    location: "canMoveUsers",
    user_package: "canModifyUsers",
} as const;

const skipUnlockingCell = (key: UsersSheetColumnKey, permissions: ExcelPermissions): boolean => {
    if (key === "short_number") return true;

    const permissionKey = USERS_CELL_UNLOCK_PERMISSION_MAP[key];
    if (permissionKey) {
        return !permissions[permissionKey];
    }
    return false;
};

const createColumns = <HeaderKeys extends string>(...headers: {header: string; key: HeaderKeys}[]) =>
    headers.map(({header, key}) => ({
        header,
        key,
        ...COLUMN_FORMAT,
    }));

/**
 * Download the excel file containing the extensions and translations
 *
 * @param users Current extensions
 * @param translations Available translations
 * @param locations Available locations
 * @param intl Intl object to format messages
 */
export const downloadExcel = async (
    users: User[],
    translations: string[],
    locations: LocationsQuery["locations"]["items"],
    vendors: string[],
    userPackages: DomainBulkExtensionQueryQuery["domainAllocation"]["userPackages"],
    intl: IntlShape,
    metadata: {
        domainName: string;
        timeOfGeneration: DateTime;
    },
    permissions: ExcelPermissions,
): Promise<void> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const workbook = new Workbook();

    const usersSheet = workbook.addWorksheet("Users");
    const translationsSheet = workbook.addWorksheet("Translations");
    const locationsSheet = workbook.addWorksheet("Locations");
    const vendorsSheet = workbook.addWorksheet("Vendors");
    const userPackagesSheet = workbook.addWorksheet("User Packages");
    const metadataSheet = workbook.addWorksheet("Metadata");

    // just to keep the excel file clean and avoid import errors, let's protect the sheets
    // according to the role the downloader has
    const pw = getRandomString(20);

    translationsSheet.protect(pw, {});
    vendorsSheet.protect(pw, {});
    userPackagesSheet.protect(pw, {});
    metadataSheet.protect(pw, {});

    if (!permissions.canAddUsers) usersSheet.protect(pw, {});
    if (!permissions.canAddLocations) locationsSheet.protect(pw, {});

    const multipleLoginsText = intl.formatMessage({
        id: "bulk_user_modification.multiple_users",
        defaultMessage: "Multiple logins",
    });

    const translateUserPackage = (userPackageName: string): string =>
        intl.formatMessage(labelForUserPackage(userPackageName));

    usersSheet.columns = createColumns<UsersSheetColumnKey>(
        {
            header: intl.formatMessage({id: "short_number", defaultMessage: "Short number"}),
            key: "short_number",
        },
        {
            header: intl.formatMessage({id: "display_name", defaultMessage: "Display name"}),
            key: "display_name",
        },
        {header: intl.formatMessage({id: "e164", defaultMessage: "E.164"}), key: "e164"},
        {header: intl.formatMessage({id: "email", defaultMessage: "Email"}), key: "email"},
        {
            header: intl.formatMessage({id: "homeNumber", defaultMessage: "Home number"}),
            key: "home_humber",
        },
        {
            header: intl.formatMessage({id: "mobileNumber", defaultMessage: "Mobile number"}),
            key: "mobile_humber",
        },
        {
            header: getProvisionedColumnTitle(intl, defineMessage({id: "mac_address", defaultMessage: "MAC address"})),
            key: "provisioned_mac_address",
        },
        {
            header: getProvisionedColumnTitle(intl, defineMessage({id: "vendor", defaultMessage: "Vendor"}), true),
            key: "provisioned_vendor",
        },
        {header: intl.formatMessage({id: "language", defaultMessage: "Language"}), key: "language"},
        {
            header: intl.formatMessage({id: "location.label", defaultMessage: "Location"}),
            key: "location",
        },
        {
            header: intl.formatMessage({id: "user_package", defaultMessage: "User package"}),
            key: "user_package",
        },
    );

    for (const user of users) {
        const data = {
            short_number: user.shortNumber,
            display_name: user.displayName,
            e164: user.e164,
            email: user.loginEmail,
            home_humber: user.homeNumber,
            mobile_humber: user.mobileNumber,
            location: user.location,
            provisioned_mac_address: formatMacAddress(user.provisionedDevice?.macAddress),
            provisioned_vendor: user.provisionedDevice?.vendor,
            language: "", // leaving blank, because it makes sense for new users only
            user_package: translateUserPackage(user.userPackageName),
        } as Record<UsersSheetColumnKey, string>;
        const row = usersSheet.addRow(data);

        if (user.multipleLogins) {
            const cell = row.getCell("email");
            cell.value = multipleLoginsText;
            cell.font = {italic: true, color: {argb: "FFCC2C00"}};
        }

        // we do not want the domain admin to modify some fields as they are only
        // for roles that can add and delete users
        if (!permissions.canAddUsers && permissions.canModifyUsers) {
            for (const key of Object.keys(data)) {
                if (skipUnlockingCell(key as UsersSheetColumnKey, permissions)) continue;
                row.getCell(key).protection = {locked: false};
            }
        }
    }

    // adding 2 because of the header and skip the last extension
    // letting 500 data validated rows for now
    for (let i = 2; i < users.length + 502; i++) {
        usersSheet.getCell(i, 8).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ["Vendors!$A$2:$A$" + (vendors.length + 1)],
        };
        usersSheet.getCell(i, 9).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ["Translations!$A$2:$A$" + (translations.length + 1)],
        };
        usersSheet.getCell(i, 10).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ["Locations!$A$2:$A$" + (locations.length + 100)],
        };
        usersSheet.getCell(i, 11).dataValidation = {
            type: "list",
            allowBlank: userPackages.length === 0,
            formulae: ["'User Packages'!$A$2:$A" + (userPackages.length + 1)],
        };
    }

    translationsSheet.columns = [{header: "Available languages", key: "available_languages", width: 30}];
    for (const translation of translations) {
        translationsSheet.addRow({
            available_languages: translation,
        });
    }

    locationsSheet.columns = createColumns<LocationsSheetColumnKey>(
        {header: "Location", key: "location_name"},
        {header: "Description", key: "description"},
        {header: "Pilot number", key: "pilot_number"},
        {header: "Parallel call limit", key: "parallel_call_limit"},
        {header: "Bandwidth limit", key: "bandwidth_limit"},
        {header: "IP address", key: "ip_address"},
    );

    for (const location of locations) {
        locationsSheet.addRow({
            location_name: location.name,
            description: location.description,
            pilot_number: location.pilotNumber,
            parallel_call_limit: location.callLimit,
            bandwidth_limit: location.bandwidthLimit,
            ip_address: location.ipAddress,
        } as Record<LocationsSheetColumnKey, string | number>);
    }

    vendorsSheet.columns = [{header: "Vendor", key: "vendor", width: 30}];
    for (const vendor of vendors) {
        vendorsSheet.addRow({vendor});
    }

    userPackagesSheet.columns = [
        {header: "User package", key: "user_package", width: 30},
        {header: "Identifier", key: "identifier", width: 30},
        {header: "Allocated for domain", key: "domain_allocated", width: 15},
        {header: "Used in domain", key: "domain_used", width: 15},
    ];
    userPackages.forEach((userPackage, index) => {
        userPackagesSheet.addRow({
            user_package: translateUserPackage(userPackage.name),
            identifier: userPackage.name,
            domain_allocated: userPackage.domainAllocated,
            domain_used: {formula: `COUNTIF(Users!K2:K${EXCEL_MAX_ROWS}, A${index + 2})`},
        });
    });
    userPackagesSheet.addConditionalFormatting({
        ref: "C2:C" + (userPackages.length + 1),
        rules: [
            {
                priority: 1,
                type: "cellIs",
                operator: "greaterThan",
                formulae: ["$B2"],
                style: {
                    fill: {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: {argb: "FFFF0000"},
                    },
                },
            },
        ],
    });

    metadataSheet.columns = [
        {header: "Domain name", key: "domain_name", width: 30},
        {
            header: "Time of generation",
            key: "time_of_generation",
            width: 30,
            style: {numFmt: EXCEL_CELL_DATETIME_FORMAT},
        },
    ];
    metadataSheet.addRow({
        domain_name: metadata.domainName,
        time_of_generation: metadata.timeOfGeneration.toJSDate(),
    });

    const fileDatetime = metadata.timeOfGeneration.toFormat(EXCEL_FILE_DATETIME_FORMAT);
    const filename = `${metadata.domainName}-users-${fileDatetime}.xlsx`;

    const fileData = await workbook.xlsx.writeBuffer();
    const blob = new Blob([fileData], {type: "application/octet-stream"});
    saveAs(blob, filename);
};

/**
 * Loads the file content into a workbook
 *
 * @param file The file to load the content from
 * @returns The workbook containing the file content
 */
const loadFromFile = async (file: File): Promise<Workbook> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const loader = new Workbook();
    return await loader.xlsx.load(await file.arrayBuffer());
};

/**
 * Maps the file content to mutation-ready users
 *
 * @param file The file to load the content from
 * @param users Current users to get helping information from (multiple logins)
 * @returns The users for the mutation
 */
export const mapFileContentToUsers = async (file: File, users: User[]): Promise<UserModification[]> => {
    const workbook = await loadFromFile(file);
    const usersSheet = workbook.getWorksheet(1);
    const userPackagesSheet = workbook.getWorksheet("User Packages");
    const fileUsers: UserModification[] = [];

    usersSheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
            return;
        }
        const shortNumber: string = row.getCell(1).value?.toString();

        const provisionedDeviceMacAddress = row.getCell(7).value?.toString();
        const provisionedDeviceVendor = row.getCell(8).value?.toString();
        const provisionedDevice =
            provisionedDeviceMacAddress || provisionedDeviceVendor
                ? {macAddress: provisionedDeviceMacAddress, vendor: provisionedDeviceVendor}
                : undefined;

        const getUserPackageOriginalName = (translatedName: string): string => {
            let packageName = "";
            userPackagesSheet.eachRow((row) => {
                if (row.getCell(1).value === translatedName) {
                    packageName = row.getCell(2).value.toString() ?? "";
                }
            });

            return packageName;
        };

        const data: BulkUserModificationData = {
            shortNumber: shortNumber,
            e164: row.getCell(3).value?.toString(),
            displayName: row.getCell(2).value?.toString(),
            homeNumber: row.getCell(5).value?.toString(),
            mobileNumber: row.getCell(6).value?.toString(),
            language: row.getCell(9).value?.toString(),
            location: row.getCell(10).value?.toString(),
            provisionedDevice,
            userPackageName: getUserPackageOriginalName(row.getCell(11).value?.toString() ?? ""),
        };

        const excelEmail = removeExcelEmailLink(row.getCell(4).value);
        const currentUser = users.find((e) => e.shortNumber === shortNumber);
        if (currentUser) {
            // we do not handle the case where there are multiple users
            if (!currentUser.multipleLogins) {
                // only keep the email if it is different from the current one
                if (excelEmail && excelEmail !== currentUser.loginEmail) {
                    data.email = excelEmail;
                }
            }
        } else {
            data.email = excelEmail;
        }

        fileUsers.push(data);
    });
    return fileUsers;
};

/**
 * Maps the file content to mutation-ready locations
 *
 * @param file The file to load the content from
 * @returns The locations for the mutation
 */
export const mapFileContentToLocations = async (file: File): Promise<LocationModification[]> => {
    const workbook = await loadFromFile(file);
    const worksheet = workbook.getWorksheet(3);
    const locations: LocationModification[] = [];

    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
            return;
        }

        locations.push({
            name: row.getCell(1).value?.toString(),
            description: row.getCell(2).value?.toString(),
            pilotNumber: row.getCell(3).value?.toString(),
            callLimit: parseInt(row.getCell(4).value?.toString()),
            bandwidthLimit: parseInt(row.getCell(5).value?.toString()),
            ipAddress: row.getCell(6).value?.toString(),
        });
    });
    return locations;
};
